/* eslint-disable */
import { useState } from 'react'
import { format } from 'date-fns'
import { nl } from 'date-fns/locale'
import {
  FileText,
  User,
  Zap,
  Image,
  ChevronRight,
  ChevronDown,
  Loader2,
  XIcon,
  ChevronLeft
} from 'lucide-react'
import { Button } from '@/components/customer-ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/customer-ui/table'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible'
import client from '@/http/customer-client'
import { Report, reportSchema } from '../../../page-report-overview/schema'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { cn } from '@/lib/utils'

const formatDate = (dateStr: string) => {
  try {
    return format(new Date(dateStr), 'd MMMM yyyy', { locale: nl })
  } catch {
    return dateStr
  }
}

const formatRecordingDate = (report: Report) => {
  const registrationDate = report.stations_snapshot?.[0]?.registration_date

  return registrationDate ? formatDate(registrationDate) : '—'
}

const formatQuarterTitle = (report: Report) => {
  const name = report.report_name || '—'
  const from = report.report_from ? formatDate(report.report_from) : '—'
  const to = report.report_to ? formatDate(report.report_to) : '—'

  return `${name} — ${from} - ${to}`
}

const formatCurrency = (value: number | string | null | undefined) => {
  if (value === null || value === undefined) {
    return '—'
  }

  return `€\u00A0${value}`
}

const fetchQuarters = async () => {
  const response = await client.get('/app-api/me/reports')

  const parsed = z
    .object({
      data: z.array(reportSchema)
    })
    .parse(response.data)

  return parsed.data
}

const QuarterDetailView = ({
  quarter,
  onBack
}: {
  quarter: Report
  onBack: () => void
}) => {
  const { t } = useTranslation()
  const info = quarter.customer_snapshot

  return (
    <div className='animate-fade-in space-y-6'>
      <div className='flex items-center gap-1'>
        <Button variant='ghost' size='icon' onClick={onBack} className='flex justify-center items-center'>
          <ChevronLeft className='text-muted-foreground size-4' />
        </Button>
        <h3 className='text-foreground text-lg font-semibold'>
          {formatQuarterTitle(quarter)}
        </h3>
      </div>

      {/* Summary bar */}
      <div className='grid grid-cols-2 gap-4 sm:grid-cols-5'>
        {[
          {
            label: t('Geladen kWh'),
            value:
              quarter.total_volume_all_stations_kwh !== null
                ? `${quarter.total_volume_all_stations_kwh} kWh`
                : '—'
          },
          {
            label: t("Ingeboekte ERE's"),
            value: quarter.booked_eres !== null ? `${quarter.booked_eres}` : '—'
          },
          {
            label: t('Opbrengst'),
            value: formatCurrency(quarter.compensation)
          },
          {
            label: t('Betaling'),
            value: formatCurrency(quarter.payment)
          },
          {
            label: t('Betaaldatum'),
            value: quarter.payment_date ? formatDate(quarter.payment_date) : '—'
          }
        ].map((item) => (
          <div
            key={item.label}
            className='bg-card space-y-1 rounded-lg border p-3 text-center'
          >
            <p className='text-muted-foreground text-xs'>{item.label}</p>
            <p className='text-foreground text-sm font-semibold'>
              {item.value}
            </p>
          </div>
        ))}
      </div>

      <Collapsible className='bg-card border-input overflow-hidden rounded-lg border'>
        <CollapsibleTrigger className='bg-muted/30 hover:bg-muted/50 flex w-full items-center justify-between px-4 py-3 transition-colors'>
          <div className='flex items-center gap-2'>
            <User className='text-primary h-4 w-4' />
            <h4 className='text-foreground font-medium'>
              {t('General information')}
            </h4>
          </div>
          <ChevronDown className='text-muted-foreground h-4 w-4 transition-transform duration-200 [[data-state=open]>&]:rotate-180' />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Table>
            <TableBody>
              {[
                { label: t('Name'), value: info.full_name },
                { label: t('Email'), value: info.email },
                {
                  label: t('Address'),
                  value:
                    info.street && info.postcode && info.place
                      ? `${info.street}, ${info.postcode}, ${info.place}`
                      : '—'
                },
                { label: t('Phone number'), value: info.phone_number },
                { label: t('EAN-18 number'), value: info.ean_number },
                {
                  label: t('Account holder name'),
                  value: info.account_holder_name
                },
                { label: t('IBAN number'), value: info.iban_number },
                ...(info.comments
                  ? [{ label: t('Comments'), value: info.comments }]
                  : [])
              ].map((f) => (
                <TableRow key={f.label}>
                  <TableCell className='text-muted-foreground w-1/3 font-medium'>
                    {f.label}
                  </TableCell>
                  <TableCell
                    className={cn(
                      f.label === t('Comments') && 'whitespace-break-spaces'
                    )}
                  >
                    {f.value || '—'}
                  </TableCell>
                </TableRow>
              ))}
              {quarter.documents_snapshot &&
                quarter.documents_snapshot.length > 0 && (
                  <TableRow>
                    <TableCell className='text-muted-foreground w-1/3 font-medium'>
                      {t('Uploaded photos')}
                    </TableCell>
                    <TableCell>
                      <div className='flex flex-wrap gap-2'>
                        {quarter.documents_snapshot.map((photo, pi) => (
                          <a
                            key={pi}
                            href={photo.url as string}
                            download={photo.path as string}
                            className='bg-muted/50 hover:bg-muted flex cursor-pointer items-center gap-2 rounded-md px-3 py-1 text-sm transition-colors'
                          >
                            <Image className='text-primary h-4 w-4 shrink-0' />
                            <span className='text-foreground underline'>
                              {photo.path as string}
                            </span>
                          </a>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          </Table>
        </CollapsibleContent>
      </Collapsible>

      {/* Charging stations (read-only) */}
      {quarter.stations_snapshot?.map((station, index) => {
        return (
          <div
            key={index}
            className='bg-card overflow-hidden rounded-lg border'
          >
            <div className='bg-muted/30 flex items-center gap-2 border-b px-4 py-3'>
              <Zap className='text-primary h-4 w-4' />
              <h4 className='text-foreground font-medium'>
                {t('Charging station')} {index + 1}
              </h4>
            </div>
            <Table>
              <TableBody>
                {[
                  {
                    label: t('Charging station (brand, model, ID)'),
                    value: `${station.brand ?? '—'}, ${station.model ?? '—'}, ${station.evse_id ?? '—'}`
                  },
                  {
                    label: t('Charged kWh'),
                    value:
                      station.total_volume_kwh == null
                        ? '—'
                        : `${station.total_volume_kwh} kWh`
                  },
                  {
                    label: t('Registration date'),
                    value: station.registration_date
                      ? formatDate(station.registration_date)
                      : '—'
                  }
                ].map((f) => (
                  <TableRow key={f.label}>
                    <TableCell className='text-muted-foreground w-1/3 font-medium'>
                      {f.label}
                    </TableCell>
                    <TableCell>{f.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      })}
    </div>
  )
}

const QuarterlyReportsTab = () => {
  const { t } = useTranslation()
  const [selectedQuarter, setSelectedQuarter] = useState<Report | null>(null)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['customer-reports'],
    queryFn: fetchQuarters
  })

  if (selectedQuarter) {
    return (
      <QuarterDetailView
        quarter={selectedQuarter}
        onBack={() => {
          setSelectedQuarter(null)
        }}
      />
    )
  }

  if (isLoading) {
    return (
      <div className='bg-card flex flex-col items-center justify-center gap-8 rounded-lg p-4 py-24 shadow-sm'>
        <Loader2 className='text-primary size-12 animate-spin' />
        <span>{t('Loading quarterly reports...')}</span>
      </div>
    )
  }

  if (isError) {
    return (
      <div className='bg-card flex flex-col items-center justify-center gap-8 rounded-lg p-4 py-24 shadow-sm'>
        <XIcon className='text-destructive size-12' />
        <span className='text-destructive'>
          {t('Failed to load quarterly reports.')}
        </span>
      </div>
    )
  }

  return (
    <div className='animate-fade-in space-y-4'>
      <h3 className='text-foreground mb-8 text-lg font-semibold'>
        {t('Reports')}
      </h3>

      {data?.length === 0 ? (
        <div className='bg-card border-input rounded-lg border p-8 text-center sm:p-12'>
          <FileText className='text-muted-foreground/40 mx-auto mb-4 h-12 w-12' />
          <p className='text-muted-foreground'>
            {t('No reports available yet.')}
          </p>
        </div>
      ) : (
        <div className='bg-card border-input overflow-hidden rounded-lg border'>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className='whitespace-nowrap'>{t('Name')}</TableHead>
                <TableHead className='whitespace-nowrap text-center'>
                  {t('Geladen kWh')}
                </TableHead>
                <TableHead className='whitespace-nowrap text-center'>
                  {t('Opnamedatum')}
                </TableHead>
                <TableHead className='whitespace-nowrap text-center'>
                  {t("Ingeboekte ERE's")}
                </TableHead>
                <TableHead className='whitespace-nowrap text-center'>
                  {t('Opbrengst')}
                </TableHead>
                <TableHead className='whitespace-nowrap text-center'>
                  {t('Betaling')}
                </TableHead>
                <TableHead className='whitespace-nowrap text-center'>
                  {t('Betaaldatum')}
                </TableHead>
                <TableHead className='whitespace-nowrap text-right'></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.map((q) => (
                <TableRow
                  key={q.id}
                  className='cursor-pointer'
                  onClick={() => {
                    setSelectedQuarter(q)
                  }}
                >
                  <TableCell className='whitespace-nowrap font-medium'>
                    {q.report_name || '—'}
                  </TableCell>
                  <TableCell className='whitespace-nowrap text-right'>
                    {q.total_volume_all_stations_kwh !== null
                      ? `${q.total_volume_all_stations_kwh} kWh`
                      : '—'}
                  </TableCell>
                  <TableCell className='whitespace-nowrap text-right'>
                    {formatRecordingDate(q)}
                  </TableCell>
                  <TableCell className='whitespace-nowrap text-right'>
                    {q.booked_eres !== null ? `${q.booked_eres}` : '—'}
                  </TableCell>
                  <TableCell className='whitespace-nowrap text-right'>
                    {formatCurrency(q.compensation)}
                  </TableCell>
                  <TableCell className='whitespace-nowrap text-right'>
                    {formatCurrency(q.payment)}
                  </TableCell>
                  <TableCell className='whitespace-nowrap text-right'>
                    {q.payment_date ? formatDate(q.payment_date) : '—'}
                  </TableCell>
                  <TableCell className='whitespace-nowrap text-right'>
                    <ChevronRight className='text-muted-foreground ml-auto size-4' />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

export default QuarterlyReportsTab
